import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";

import { ISelfOccupiedProperties } from "../../../../config/interfaces";
import FASvgIcon from "../../../FASvgIcon";
import { NumericInputField, TextInputField } from "../../../fields";
import PreventEditComponent from "../../../PreventEditComponent";

const RISelfOwnerDetailsForm: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<ISelfOccupiedProperties>();

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

  useEffect(() => {
    calculatePercentage();
  }, [currentTime]);

  const calculatePercentage = () => {
    let sum = values.OwnerDetails.slice(1).reduce(
      (acc, item) => acc + (parseFloat(item.Percentage) || 0),
      0
    );
    const firstOwnerPercentage = 100 - sum;
    setFieldValue(`OwnerDetails.${0}.Percentage`, firstOwnerPercentage);

    if (sum > 100) {
      setErrorMessage("Sum of percentage share should always be 100");
    } else if (firstOwnerPercentage === 0) {
      setErrorMessage("First owner's percentage cannot be zero");
    } else {
      setErrorMessage("");
    }
  };

  return (
    <section className="mb-5">
      <Card className="more-info-card shadow-none">
        <Card.Header>
          <Card.Title>Owner Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <section>
            {errorMessage && (
              <Alert variant="danger" className="mb-4 size-sm">
                <h5 className="alert-title">{errorMessage}</h5>
              </Alert>
            )}
            <Row className="align-items-center">
              <Col sm>
                <TextInputField
                  label="Name of Owner"
                  name={`OwnerDetails.${0}.FullName`}
                  value={values.OwnerDetails[0].FullName}
                  isRequired={true}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue(
                      `OwnerDetails.${0}.FullName`,
                      values.OwnerDetails[0].FullName?.trim()
                    );
                  }}
                  onChange={(value) => setFieldValue(`OwnerDetails.${0}.FullName`, value)}
                  isInvalid={
                    Boolean(getIn(errors, `OwnerDetails.${0}.FullName`)) &&
                    getIn(touched, `OwnerDetails.${0}.FullName`)
                  }
                  errorMsg={getIn(errors, `OwnerDetails.${0}.FullName`)}
                />
              </Col>
              <Col sm>
                <TextInputField
                  label="PAN of Owner"
                  name={`OwnerDetails.${0}.Pan`}
                  maxLength={10}
                  value={values.OwnerDetails[0].Pan}
                  isRequired={true}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  onChange={(value) => setFieldValue(`OwnerDetails.${0}.Pan`, value.toUpperCase())}
                  isInvalid={
                    Boolean(getIn(errors, `OwnerDetails.${0}.Pan`)) &&
                    getIn(touched, `OwnerDetails.${0}.Pan`)
                  }
                  errorMsg={getIn(errors, `OwnerDetails.${0}.Pan`)}
                />
              </Col>
              <Col sm>
                <NumericInputField
                  showRightIcon={true}
                  label="Share (in %)"
                  name={`OwnerDetails.${0}.Percentage`}
                  value={values.OwnerDetails[0].Percentage}
                  onBlur={handleBlur(`OwnerDetails.${0}.Percentage`)}
                  onChange={(value) => setFieldValue(`OwnerDetails.${0}.Percentage`, value)}
                  isInvalid={
                    Boolean(getIn(errors, `OwnerDetails.${0}.Percentage`)) &&
                    getIn(touched, `OwnerDetails.${0}.Percentage`)
                  }
                  errorMsg={getIn(errors, `OwnerDetails.${0}.Percentage`)}
                  isDisabled={true}
                />
              </Col>
            </Row>
          </section>
          <section>
            {values?.OwnerDetails?.length > 1 ? (
              <div className="section-title-wrap">
                <h5>
                  Co-Owner(s)
                  <small>Please ensure total % of ownership of all owners adds up to 100%</small>
                </h5>
              </div>
            ) : null}
            <FieldArray name="OwnerDetails">
              {({ push, remove, form, replace }) => (
                <>
                  {values?.OwnerDetails?.map((_, index) => {
                    if ([0].includes(index)) {
                      return null;
                    }

                    return (
                      <section className="mb-3 mb-sm-0" key={`owner-${index}`}>
                        <OwnerForm
                          index={index}
                          form={form}
                          onRemove={(itemIdx: number) => {
                            if (values.OwnerDetails.length === 1) {
                              replace(itemIdx, { FullName: "", Pan: "", Percentage: "" });
                            } else {
                              remove(itemIdx);
                            }
                            setCurrentTime(new Date().getTime());
                          }}
                          handlePercentageChange={() => setCurrentTime(new Date().getTime())}
                        />
                      </section>
                    );
                  })}
                  <PreventEditComponent>
                    <Button
                      variant="link"
                      className="btn-add-link"
                      onClick={() =>
                        push({
                          FullName: "",
                          Pan: "",
                          Percentage: ""
                        })
                      }
                      disabled={!(Number(values.OwnerDetails[0].Percentage) > 0)}
                    >
                      <FASvgIcon icon={faCirclePlus} />
                      Add more
                    </Button>
                  </PreventEditComponent>
                </>
              )}
            </FieldArray>
          </section>
        </Card.Body>
      </Card>
    </section>
  );
};

export default RISelfOwnerDetailsForm;

type Props = {
  index: number;
  form: FormikProps<ISelfOccupiedProperties>;
  onRemove: (itemIdx: number) => void;
  handlePercentageChange: () => void;
};

const OwnerForm: React.FC<Props> = (props: Props) => {
  const { index, onRemove, form, handlePercentageChange } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <Row className="align-items-center">
        <Col sm={4}>
          <TextInputField
            label="Name of the Co-Owner"
            name={`OwnerDetails.${index}.FullName`}
            value={values.OwnerDetails[index].FullName}
            isRequired={true}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `OwnerDetails.${index}.FullName`,
                values.OwnerDetails[index].FullName?.trim()
              );
            }}
            onChange={(value) => setFieldValue(`OwnerDetails.${index}.FullName`, value)}
            isInvalid={
              Boolean(getIn(errors, `OwnerDetails.${index}.FullName`)) &&
              getIn(touched, `OwnerDetails.${index}.FullName`)
            }
            errorMsg={getIn(errors, `OwnerDetails.${index}.FullName`)}
          />
        </Col>
        <Col sm={4}>
          <TextInputField
            label="PAN of the Co-Owner"
            name={`OwnerDetails.${index}.Pan`}
            maxLength={10}
            value={values.OwnerDetails[index].Pan}
            isRequired={true}
            onBlur={(e) => {
              handleBlur(e);
            }}
            onChange={(value) => setFieldValue(`OwnerDetails.${index}.Pan`, value.toUpperCase())}
            isInvalid={
              Boolean(getIn(errors, `OwnerDetails.${index}.Pan`)) &&
              getIn(touched, `OwnerDetails.${index}.Pan`)
            }
            errorMsg={getIn(errors, `OwnerDetails.${index}.Pan`)}
          />
        </Col>
        <Col sm>
          <NumericInputField
            showRightIcon={true}
            label="Share (in %)"
            maxLength={5}
            name={`OwnerDetails.${index}.Percentage`}
            value={values.OwnerDetails[index].Percentage}
            isRequired={true}
            onBlur={() => {
              handleBlur(`OwnerDetails.${index}.Percentage`);
            }}
            onChange={(value) => {
              setFieldValue(`OwnerDetails.${index}.Percentage`, value);
              handlePercentageChange();
            }}
            isInvalid={
              Boolean(getIn(errors, `OwnerDetails.${index}.Percentage`)) &&
              getIn(touched, `OwnerDetails.${index}.Percentage`)
            }
            errorMsg={getIn(errors, `OwnerDetails.${index}.Percentage`)}
          />
        </Col>
        <PreventEditComponent>
          <Col sm={"auto"} className="ps-0">
            <Button
              variant="transparent"
              className="text-greyDark icon-only d-none d-sm-flex"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
            </Button>
            <Button
              variant="transparent"
              className="btn-delete-full d-sm-none"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
              <span>Delete</span>
            </Button>
          </Col>
        </PreventEditComponent>
      </Row>
      {index !== values.OwnerDetails.length - 1 && <hr className="d-block d-sm-none my-4" />}
    </>
  );
};
